import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  isThemePreference,
  THEME_STORAGE_KEY,
  ThemeContext,
  type ResolvedTheme,
  type ThemeContextValue,
  type ThemePreference,
} from './context';

/**
 * Tema sağlayıcı.
 *
 * ## Renkleri BU dosya belirlemez
 *
 * Tek yaptığı `<html data-theme>` özniteliğini yönetmek. Renkler
 * `src/index.css` içindeki `light-dark()` token'larından gelir ve
 * `color-scheme` ile çözülür. Bu ayrım bilinçli: renk kararı CSS'te kalırsa
 * JavaScript yüklenmeden ÖNCE doğru tema boyanır.
 *
 * ## Varsayılan açık tema
 *
 * Tercih kaydedilmemişse tema AÇIKTIR (stüdyonun kararı: site koyu modlu
 * telefonda da açık zeminde açılsın). `:root` varsayılanı `color-scheme:
 * light` olduğu için bu JavaScript'ten önce, CSS ile çözülür; açık temayla
 * açılan çoğunluk için yanlış renkle boyanıp düzelme (FOUC) yok. Koyu temayı
 * SEÇMİŞ ziyaretçi ilk karede açık zemini kısa bir an görebilir — aşağıdaki
 * CSP gerekçesi yüzünden kabul edilen bedel.
 *
 * `system` hâlâ seçilebilir (panel tema seçicisi); o zaman `data-theme=system`
 * yazılır ve CSS işletim sisteminin ayarını izler.
 *
 * Bu aynı zamanda bir CSP kararıdır: FOUC'u engellemenin yaygın yolu
 * `index.html` içine satır içi bir script koymaktır, ama üretimde CSP nonce
 * gerektirir ve `index.html` statik dosya olarak servis edildiği için nonce
 * alamaz — o script sessizce bloklanır ve çözüm hiç çalışmazdı.
 */

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && isThemePreference(stored)) return stored;
  } catch {
    /* Gizli sekmede veya depolama izni kapalıyken erişimin KENDİSİ fırlatır;
       yakalanmazsa uygulama hiç açılmaz. Varsayılana düşmek yeterli. */
  }
  return 'light';
}

function readSystemTheme(): ResolvedTheme {
  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(readSystemTheme);

  /* Sistem tercihi oturum sırasında değişebilir (macOS/Windows gün batımında
     otomatik geçer). `system` seçiliyken bunu izlemezsek uygulama, işletim
     sistemi koyuya geçtikten sonra tek başına aydınlık kalır. */
  useEffect(() => {
    const query = window.matchMedia(SYSTEM_DARK_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  const resolved: ResolvedTheme = preference === 'system' ? systemTheme : preference;

  /* Açık tema CSS varsayılanı olduğu için öznitelik kaldırılır; `dark` ve
     `system` yazılır (`system` → CSS `color-scheme: light dark`). */
  useEffect(() => {
    const root = document.documentElement;
    if (preference === 'light') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', preference);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Kaydedilemiyorsa tercih oturum boyunca geçerli kalır; hata gösterilmez.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
